import { Injectable } from '@angular/core';
import { AuthService } from 'src/app/features/auth/auth.service';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class AccountTypeService {
    // Mapeamento de ID para nome do tipo de conta
    private readonly ACCOUNT_TYPE_MAP: { [key: number]: string } = {
        1: 'ASSOCIAÇÃO',
        2: 'PADRÃO',
        3: 'MGA'
    };

    constructor(private authService: AuthService) { }

    /**
     * Obtém o ID do tipo de conta (do cache ou do banco)
     * Retorna um Observable com o ID numérico: 1 (ASSOCIAÇÃO), 2 (PADRÃO) ou 3 (MGA)
     */
    getAccountType(): Observable<number> {
        // Primeiro verifica se já tem no sessionStorage
        const cachedAccountType = sessionStorage.getItem('account_type');

        // Se o cache tem um valor válido (número) e a integração OON já foi cacheada, retorna imediatamente
        if (cachedAccountType && ['1', '2', '3'].includes(cachedAccountType) && this.hasCachedOonIntegration() && this.hasCachedSimplificado()) {
            return of(parseInt(cachedAccountType, 10));
        }

        // Se não tem no cache ou tem valor antigo (nome), busca do banco
        return this.fetchAccountData().pipe(map((data) => data.accountTypeId));
    }

    /**
     * Informa se a conta possui a integração OON habilitada (do cache ou do banco)
     */
    getOonIntegrationEnabled(): Observable<boolean> {
        if (this.hasCachedOonIntegration()) {
            return of(this.isOonIntegrationEnabled());
        }

        return this.fetchAccountData().pipe(map((data) => data.oonIntegrationEnabled));
    }

    /**
     * Informa se a conta está no modo simplificado (do cache ou do banco)
     */
    getSimplificado(): Observable<boolean> {
        if (this.hasCachedSimplificado()) {
            return of(this.isSimplificado());
        }

        return this.fetchAccountData().pipe(map((data) => data.simplificado));
    }

    /**
     * Busca tipo de conta, integração OON e modo simplificado no banco e grava no sessionStorage
     */
    private fetchAccountData(): Observable<{ accountTypeId: number, oonIntegrationEnabled: boolean, simplificado: boolean }> {
        return new Observable(observer => {
            this.authService.getAccountType().subscribe({
                next: (response) => {
                    const accountTypeId = response.account_type || 2; // Default: PADRÃO
                    const oonIntegrationEnabled = !!response.oon_integration_enabled;
                    const simplificado = !!response.modo_simplificado;

                    // Salva no sessionStorage
                    sessionStorage.setItem('account_type', accountTypeId.toString());
                    this.setCachedOonIntegrationEnabled(oonIntegrationEnabled);
                    this.setCachedSimplificado(simplificado);

                    observer.next({ accountTypeId, oonIntegrationEnabled, simplificado });
                    observer.complete();
                },
                error: (error) => {
                    console.error('Erro ao buscar account_type:', error);
                    // Em caso de erro, assume PADRÃO (2) sem integração OON e sem modo simplificado
                    sessionStorage.setItem('account_type', '2');
                    this.setCachedOonIntegrationEnabled(false);
                    this.setCachedSimplificado(false);
                    observer.next({ accountTypeId: 2, oonIntegrationEnabled: false, simplificado: false });
                    observer.complete();
                }
            });
        });
    }

    /**
     * Grava o tipo de conta desta aba a partir do nome do tipo
     * ('ASSOCIAÇÃO', 'PADRÃO' ou 'MGA')
     */
    setCachedAccountTypeByName(accountTypeName: string): void {
        const accountTypeId = Object.keys(this.ACCOUNT_TYPE_MAP)
            .find((id) => this.ACCOUNT_TYPE_MAP[Number(id)] === accountTypeName)

        sessionStorage.setItem('account_type', accountTypeId || '2') // Default: PADRÃO
    }

    /**
     * Grava nesta aba se a conta possui a integração OON habilitada
     */
    setCachedOonIntegrationEnabled(enabled: boolean): void {
        sessionStorage.setItem('oon_integration_enabled', enabled ? '1' : '0')
    }

    /**
     * Grava nesta aba se a conta está no modo simplificado
     */
    setCachedSimplificado(enabled: boolean): void {
        sessionStorage.setItem('modo_simplificado', enabled ? '1' : '0')
    }

    /**
     * Obtém o ID do tipo de conta do sessionStorage (síncrono)
     * @returns 1 (ASSOCIAÇÃO), 2 (PADRÃO) ou 3 (MGA)
     */
    getCachedAccountTypeId(): number {
        const cached = sessionStorage.getItem('account_type');
        return cached ? parseInt(cached, 10) : 2; // Default: PADRÃO
    }

    /**
     * Obtém o nome do tipo de conta do sessionStorage (síncrono)
     * @returns 'PADRÃO', 'ASSOCIAÇÃO' ou 'MGA'
     */
    getCachedAccountTypeName(): string {
        const id = this.getCachedAccountTypeId();
        return this.ACCOUNT_TYPE_MAP[id] || 'PADRÃO';
    }

    /**
     * Verifica se é conta ASSOCIAÇÃO
     */
    isAssociacao(): boolean {
        return this.getCachedAccountTypeId() === 1;
    }

    /**
     * Verifica se é conta PADRÃO
     */
    isPadrao(): boolean {
        return this.getCachedAccountTypeId() === 2;
    }

    /**
     * Verifica se é conta MGA
     */
    isMga(): boolean {
        return this.getCachedAccountTypeId() === 3;
    }

    /**
     * Verifica se a conta possui a integração OON habilitada (síncrono)
     */
    isOonIntegrationEnabled(): boolean {
        return sessionStorage.getItem('oon_integration_enabled') === '1';
    }

    /**
     * Verifica se a conta está no modo simplificado (síncrono)
     */
    isSimplificado(): boolean {
        return sessionStorage.getItem('modo_simplificado') === '1';
    }

    /**
     * Verifica se a integração OON já foi cacheada nesta aba
     */
    private hasCachedOonIntegration(): boolean {
        const cached = sessionStorage.getItem('oon_integration_enabled');
        return cached === '1' || cached === '0';
    }

    /**
     * Verifica se o modo simplificado já foi cacheado nesta aba
     */
    private hasCachedSimplificado(): boolean {
        const cached = sessionStorage.getItem('modo_simplificado');
        return cached === '1' || cached === '0';
    }

    /**
     * Limpa o cache (usar no logout)
     */
    clearCache(): void {
        sessionStorage.removeItem('account_type');
        sessionStorage.removeItem('oon_integration_enabled');
        sessionStorage.removeItem('modo_simplificado');
    }
}